import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Switch, Select, Segmented, Button, Tag, Statistic, Row, Col,
  Space, Tooltip, Popconfirm, Input, Divider, Empty, Skeleton, Badge, Modal,
} from 'antd';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  RefreshCw, Play, Save, Send, Download, ShieldCheck, ShieldAlert,
  CalendarClock, Users, FileText, Clock, Building2, AlertTriangle, Mail, Paperclip,
} from 'lucide-react';

import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { getAdminDesignSystem, ADMIN_ROOT, AdminThemeProvider, getAdminTokens } from './adminDesignSystem';
import AdminSidebar from './AdminSidebar';

const API = '/api/v1/weekly-reports';

const STATUS = {
  generating: { color: 'processing', label: 'Generating…' },
  generated: { color: 'gold', label: 'Draft · review' },
  approved: { color: 'blue', label: 'Approved' },
  sent: { color: 'green', label: 'Sent' },
  failed: { color: 'red', label: 'Failed' },
  skipped: { color: 'default', label: 'No activity' },
};

const CRON_PRESETS = [
  { label: 'Saturday, 3:00 AM (night · recommended)', value: '0 3 * * 6' },
  { label: 'Friday, 11:00 PM (night)', value: '0 23 * * 5' },
  { label: 'Saturday, 1:00 AM (night)', value: '0 1 * * 6' },
  { label: 'Sunday, 2:00 AM (night)', value: '0 2 * * 0' },
];

const TZs = ['Asia/Kolkata', 'Europe/Dublin', 'Europe/London', 'America/New_York', 'UTC'];

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// turn a "m h * * d" cron into "Every Saturday at 3:00 AM"; falls back to raw for anything exotic
const cronToText = (expr) => {
  if (!expr) return '—';
  const p = String(expr).trim().split(/\s+/);
  if (p.length !== 5) return expr;
  const [min, hr, dom, , dow] = p;
  const m = parseInt(min, 10), h = parseInt(hr, 10);
  if (isNaN(m) || isNaN(h)) return expr;
  const time = `${((h % 12) || 12)}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
  let day = 'every day';
  if (dow !== '*' && !isNaN(parseInt(dow, 10))) day = `every ${DOW[parseInt(dow, 10) % 7]}`;
  else if (dom !== '*') day = `on day ${dom} of the month`;
  return `${day[0].toUpperCase()}${day.slice(1)} at ${time}`;
};
const isNight = (expr) => {
  const h = parseInt(String(expr).trim().split(/\s+/)[1], 10);
  return !isNaN(h) && (h >= 21 || h <= 5);
};

const KpiCard = ({ icon: Icon, label, value, tone, sub, tk }) => (
  <div className="wr-kpi" style={{ background: tk?.panel, border: `1px solid ${tk?.border}`, boxShadow: tk?.sh }}>
    <div className="wr-kpi-head">
      <span className="wr-kpi-ico" style={{ background: tone.bg, color: tone.fg }}>
        <Icon size={15} strokeWidth={2.2} />
      </span>
      <span className="wr-kpi-label" style={{ color: tk?.text3 }}>{label}</span>
    </div>
    <div className="wr-kpi-value" style={{ color: tone.fg }}>{value}</div>
    <div className="wr-kpi-sub" style={{ color: tk?.text3 }}>{sub || ' '}</div>
  </div>
);

const WeeklyReports = () => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const designSystem = useMemo(() => getAdminDesignSystem(isDark), [isDark]);
  const tokens = useMemo(() => getAdminTokens(isDark), [isDark]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [history, setHistory] = useState({ byOrg: [], recent: [] });
  const [busyRow, setBusyRow] = useState(null);
  const [busyGen, setBusyGen] = useState(null);

  const tone = useMemo(() => ({
    teal: { bg: isDark ? 'rgba(0,209,193,.12)' : '#e2f7f4', fg: isDark ? '#3fe0d2' : '#00877d' },
    blue: { bg: isDark ? 'rgba(47,111,237,.14)' : '#e8f1fe', fg: isDark ? '#7aa5f7' : '#2f6fed' },
    gold: { bg: isDark ? 'rgba(184,134,11,.16)' : '#fbf1d9', fg: isDark ? '#e0b64b' : '#a5790a' },
    green: { bg: isDark ? 'rgba(26,163,99,.14)' : '#e6f7ef', fg: isDark ? '#3fc27f' : '#1aa363' },
    red: { bg: isDark ? 'rgba(229,81,75,.14)' : '#fdecea', fg: isDark ? '#f0736c' : '#e5514b' },
  }), [isDark]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o, h] = await Promise.all([
        api.get(`${API}/config`),
        api.get(`${API}/orgs`),
        api.get(`${API}/history`),
      ]);
      setConfig(c.data?.data || null);
      setOrgs(o.data?.data || []);
      setHistory(h.data?.data || { byOrg: [], recent: [] });
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Failed to load weekly-reports data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const patch = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const saveConfig = async () => {
    setSaving(true);
    try {
      const body = (({ enabled, cron, timezone, window, reviewGate, audience, selectedOrgIds, autoSendBlockOnHighFlag }) =>
        ({ enabled, cron, timezone, window, reviewGate, audience, selectedOrgIds, autoSendBlockOnHighFlag }))(config);
      const res = await api.put(`${API}/config`, body);
      setConfig(res.data?.data || config);
      toast.success('Schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post(`${API}/run`);
      const s = res.data?.data || {};
      toast.success(`Run complete — ${s.generated || 0} generated, ${s.sent || 0} sent, ${s.skipped || 0} skipped`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Run failed');
    } finally { setRunning(false); }
  };

  // Send-confirmation: fetch the exact recipient + mail draft, show it, and only send on confirm.
  const [sendModal, setSendModal] = useState({ open: false, loading: false, sending: false, id: null, row: null, draft: null });
  const openSendConfirm = async (r) => {
    setSendModal({ open: true, loading: true, sending: false, id: r._id, row: r, draft: null });
    try {
      const res = await api.get(`${API}/send-preview/${r._id}`);
      setSendModal((s) => (s.id === r._id ? { ...s, loading: false, draft: res.data?.data } : s));
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Could not load the mail draft');
      setSendModal({ open: false, loading: false, sending: false, id: null, row: null, draft: null });
    }
  };
  const closeSendConfirm = () => setSendModal({ open: false, loading: false, sending: false, id: null, row: null, draft: null });

  const sendLog = async (id) => {
    setSendModal((s) => ({ ...s, sending: true }));
    setBusyRow(id);
    try {
      await api.post(`${API}/send/${id}`);
      toast.success('Report sent');
      closeSendConfirm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Send failed');
      setSendModal((s) => ({ ...s, sending: false }));
    } finally { setBusyRow(null); }
  };

  const generateOne = async (orgId) => {
    setBusyGen(orgId);
    const t = toast.loading('Generating report… you can keep working or refresh — it runs in the background.');
    try {
      const res = await api.post(`${API}/generate/${orgId}`);
      const draft = res.data?.data;         // 'generating' placeholder — already saved server-side
      fetchAll();                            // show it in the table right away
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 150000) { // safety cap
          toast('Still generating — it will appear when ready.', { id: t, icon: '⏳' });
          setBusyGen(null); fetchAll(); return;
        }
        let cur = null;
        try {
          const lg = (await api.get(`${API}/logs`, { params: { orgId } })).data?.data || [];
          cur = lg.find((x) => x._id === draft?._id) || lg[0];
        } catch (_) { /* transient — keep polling */ }
        if (!cur || cur.status === 'generating') { setTimeout(poll, 3000); return; }
        if (cur.status === 'skipped') toast.error('No activity in the window — nothing to report.', { id: t });
        else if (cur.status === 'failed') toast.error(`Generation failed: ${cur.error || 'unknown error'}`, { id: t });
        else {
          toast.success(`Report ready — ${cur.stats?.peopleActive ?? 0} people, ${cur.stats?.redFlags ?? 0} flags`, { id: t });
          preview(cur);
        }
        setBusyGen(null); fetchAll();
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Generate failed', { id: t });
      setBusyGen(null);
    }
  };

  // Open the report. Opens the tab synchronously (so popup-blockers don't kill it), then fills it:
  // a signed S3 URL if a stored artifact exists, otherwise the inline HTML kept in Mongo.
  const preview = async (r) => {
    const w = window.open('', '_blank');
    if (w) w.document.write('<!doctype html><title>Loading report…</title><body style="font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#5a6675">Preparing report preview…</body>');
    try {
      // prefer the exact print-quality PDF when one is stored inline
      if (r.hasPdf) {
        try {
          const res = await api.get(`${API}/view-pdf/${r._id}`, { responseType: 'arraybuffer' });
          const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
          if (w) w.location = url; else window.open(url, '_blank');
          return;
        } catch (pdfErr) { /* fall back to HTML preview below */ }
      }
      if (r.s3PdfKey || r.s3HtmlKey) {
        const res = await api.get(`${API}/download/${r._id}`, { params: { type: r.s3PdfKey ? 'pdf' : 'html' } });
        const url = res.data?.data?.url;
        if (url) { if (w) w.location = url; else window.open(url, '_blank'); return; }
      }
      const res = await api.get(`${API}/view/${r._id}`);
      const html = res.data?.data?.html;
      if (!html) { if (w) w.close(); toast.error('No preview yet — click Generate again.'); return; }
      if (w) { w.document.open(); w.document.write(html); w.document.close(); }
      else {
        const blob = new Blob([html], { type: 'text/html' });
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch (e) {
      if (w) w.close();
      toast.error(e.response?.data?.error?.message || 'Preview failed');
    }
  };

  const paidCount = orgs.length;
  const generatedThisWeek = history.recent.filter((r) =>
    dayjs(r.createdAt).isAfter(dayjs().startOf('week'))).length;
  const awaitingReview = history.recent.filter((r) => r.status === 'generated').length;
  const sentAllTime = history.byOrg.reduce((s, o) => s + (o.sentCount || 0), 0);

  const orgNameById = useMemo(() => Object.fromEntries(orgs.map((o) => [o.id, o.name])), [orgs]);

  const runColumns = [
    {
      title: 'Organization', dataIndex: 'organizationName', key: 'org',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v || orgNameById[r.organizationId] || r.organizationId}</div>
          <div style={{ fontSize: 12, color: tokens?.text3 || '#8a94a6' }}>{r.windowLabel}</div>
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 130,
      render: (s) => <Tag color={(STATUS[s] || {}).color}>{(STATUS[s] || {}).label || s}</Tag>,
    },
    {
      title: 'Signals', key: 'stats', width: 210,
      render: (_, r) => {
        const people = r.stats?.peopleActive ?? 0;
        const recs = ((r.stats?.activityRecords || 0) / 1000).toFixed(1);
        const high = r.stats?.highFlags || 0;
        const flags = r.stats?.redFlags || 0;
        const isGen = r.status === 'generating';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13 }}>
            <Tooltip title="People active">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.text2 }}>
                <Users size={14} /> {isGen ? '—' : people}
              </span>
            </Tooltip>
            <Tooltip title="Activity records analysed">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.text2 }}>
                <FileText size={13} /> {isGen ? '—' : `${recs}k`}
              </span>
            </Tooltip>
            {isGen ? null : high > 0
              ? <Tooltip title="High-severity finding — held for review"><Tag color="red" style={{ margin: 0 }}>{high} high</Tag></Tooltip>
              : flags > 0
                ? <Tooltip title="Findings to review"><Tag color="gold" style={{ margin: 0 }}>{flags} flags</Tag></Tooltip>
                : <Tag color="green" style={{ margin: 0 }}>clean</Tag>}
          </div>
        );
      },
    },
    {
      title: 'Sent', dataIndex: 'sentAt', key: 'sentAt', width: 140,
      render: (v) => v ? dayjs(v).format('DD MMM, HH:mm') : <span style={{ color: tokens?.text3 || '#8a94a6' }}>—</span>,
    },
    {
      title: '', key: 'actions', width: 210, align: 'right',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<Download size={13} />} onClick={() => preview(r)}
            disabled={r.status === 'generating' || r.status === 'skipped'}>Preview</Button>
          {(r.status === 'generated' || r.status === 'approved') && (
            <Button size="small" type="primary" icon={<Send size={13} />}
              loading={busyRow === r._id} onClick={() => openSendConfirm(r)}>
              Approve &amp; send
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const orgsColumns = [
    {
      title: 'Organization', dataIndex: 'name', key: 'name',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Plan', dataIndex: 'plan', key: 'plan', width: 130,
      render: (v) => <Tag color={v === 'premium' ? 'purple' : v === 'standard' ? 'blue' : 'default'} style={{ textTransform: 'capitalize' }}>{v}</Tag>,
      filters: [{ text: 'Premium', value: 'premium' }, { text: 'Standard', value: 'standard' }],
      onFilter: (val, r) => r.plan === val,
    },
    {
      title: 'Subscription', dataIndex: 'subscriptionStatus', key: 'sub', width: 140,
      render: (v) => <Tag color={v === 'active' ? 'green' : v === 'past_due' ? 'orange' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Seats', dataIndex: 'seats', key: 'seats', width: 100, align: 'right',
      sorter: (a, b) => (a.seats || 0) - (b.seats || 0),
      render: (v, r) => (
        <Tooltip title={`${r.usedSeats ?? 0} used of ${v ?? 0} paid seats`}>
          <span style={{ fontWeight: 600 }}>{v ?? '—'}</span>
          {r.usedSeats != null && r.usedSeats !== v
            ? <span style={{ color: tokens.text3, fontSize: 12 }}> · {r.usedSeats} used</span> : null}
        </Tooltip>
      ),
    },
    {
      title: '', key: 'gen', width: 150, align: 'right',
      render: (_, r) => (
        <Tooltip title="Generate this org's report now and open it">
          <Button size="small" icon={<Play size={12} />} loading={busyGen === r.id}
            onClick={() => generateOne(r.id)}>Generate now</Button>
        </Tooltip>
      ),
    },
  ];

  const historyColumns = [
    {
      title: 'Organization', dataIndex: 'organizationName', key: 'org',
      render: (v, r) => <span style={{ fontWeight: 600 }}>{v || r._id}</span>,
    },
    { title: 'Reports sent', dataIndex: 'sentCount', key: 'sent', width: 140, align: 'right',
      render: (v) => <Badge count={v} showZero color="#1aa363" overflowCount={999} /> },
    { title: 'Last sent', dataIndex: 'lastSentAt', key: 'last', width: 180,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
  ];

  const c = config || {};
  const cronOptions = (() => {
    const base = CRON_PRESETS.map((p) => ({ ...p }));
    if (c.cron && !base.some((o) => o.value === c.cron)) {
      base.unshift({ label: `${cronToText(c.cron)}${isNight(c.cron) ? '' : ' (business hours)'}`, value: c.cron });
    }
    return base;
  })();

  return (
    <>
      <style>{designSystem}</style>
      <style>{PAGE_CSS}</style>
      <AdminThemeProvider>
        <div className={`${ADMIN_ROOT} h-screen flex overflow-hidden`}>
          <AdminSidebar activeKey="weekly-reports" />
          <div className="wr-main">
            {/* header */}
            <div className="wr-header">
              <div>
                <h1 className="wr-title" style={{ color: tokens.text }}>Weekly reports</h1>
                <p className="wr-sub" style={{ color: tokens.text2 }}>Automated workforce-intelligence briefs for paid organizations — generated, stored, and reviewed before they send.</p>
              </div>
              <Space>
                <Button icon={<RefreshCw size={15} />} onClick={fetchAll} loading={loading}>Refresh</Button>
                <Popconfirm
                  title="Run this week now?"
                  description={c.reviewGate ? 'Generates drafts for every paid org. Nothing sends until you approve each one.' : 'The review gate is OFF — reports without a high flag will send immediately.'}
                  okText="Run" cancelText="Cancel" onConfirm={runNow}
                >
                  <Button type="primary" icon={<Play size={15} />} loading={running}>Run this week now</Button>
                </Popconfirm>
              </Space>
            </div>

            {/* KPIs */}
            <Row gutter={[14, 14]} className="wr-kpis">
              <Col xs={12} md={6}><KpiCard icon={Building2} label="Paid orgs" value={loading ? '—' : paidCount} tone={tone.blue} sub="in the audience" tk={tokens} /></Col>
              <Col xs={12} md={6}><KpiCard icon={FileText} label="Generated this week" value={loading ? '—' : generatedThisWeek} tone={tone.teal} sub="drafts created" tk={tokens} /></Col>
              <Col xs={12} md={6}><KpiCard icon={Clock} label="Awaiting review" value={loading ? '—' : awaitingReview} tone={tone.gold} sub="need approval" tk={tokens} /></Col>
              <Col xs={12} md={6}><KpiCard icon={Send} label="Sent all-time" value={loading ? '—' : sentAllTime} tone={tone.green} sub="delivered" tk={tokens} /></Col>
            </Row>

            {/* paid orgs */}
            <Card className="wr-card" title={<Space><Building2 size={17} /> <span>Paid organizations</span></Space>}
              extra={<Tag color="blue">{paidCount} paying</Tag>}>
              <div className="wr-field-hint" style={{ marginTop: -6, marginBottom: 12 }}>
                Active or grace-period subscriptions on a paid plan — excludes free trials and canceled accounts. This is the audience for the weekly run.
              </div>
              <Table
                rowKey="id" size="small" columns={orgsColumns} dataSource={orgs}
                loading={loading} pagination={{ pageSize: 8, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="No paying organizations found." /> }}
              />
            </Card>

            {/* schedule */}
            <Card
              className="wr-card"
              title={<Space><CalendarClock size={17} /> <span>Schedule</span></Space>}
              extra={<Button type="primary" icon={<Save size={14} />} loading={saving} onClick={saveConfig} disabled={!config}>Save changes</Button>}
            >
              {!config ? <Skeleton active /> : (
                <>
                  <div className="wr-switch-row">
                    <div>
                      <div className="wr-switch-label">
                        {c.enabled ? <Badge status="processing" /> : <Badge status="default" />} Schedule enabled
                      </div>
                      <div className="wr-switch-hint">When on, the Friday run fires automatically. When off, nothing runs on its own.</div>
                    </div>
                    <Switch checked={!!c.enabled} onChange={(v) => patch('enabled', v)} />
                  </div>
                  <Divider style={{ margin: '14px 0' }} />
                  <Row gutter={[20, 18]}>
                    <Col xs={24} md={12}>
                      <label className="wr-field-label">Cadence</label>
                      <Select
                        style={{ width: '100%' }} value={c.cron}
                        onChange={(v) => patch('cron', v)}
                        options={cronOptions}
                        popupMatchSelectWidth={false}
                      />
                      <div className="wr-field-hint" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Runs {cronToText(c.cron)}</span>
                        {isNight(c.cron)
                          ? <Tag color="green" style={{ margin: 0 }}>off-peak</Tag>
                          : <Tag color="orange" style={{ margin: 0 }}>business hours</Tag>}
                      </div>
                    </Col>
                    <Col xs={24} md={12}>
                      <label className="wr-field-label">Timezone</label>
                      <Select style={{ width: '100%' }} value={c.timezone} onChange={(v) => patch('timezone', v)}
                        options={TZs.map((t) => ({ label: t, value: t }))} showSearch />
                    </Col>
                    <Col xs={24} md={12}>
                      <label className="wr-field-label">Report window</label>
                      <Segmented block value={c.window} onChange={(v) => patch('window', v)}
                        options={[{ label: 'Last 7 days', value: 'rolling7' }, { label: 'Last 14 days', value: 'rolling14' }]} />
                    </Col>
                    <Col xs={24} md={12}>
                      <label className="wr-field-label">Audience</label>
                      <Segmented block value={c.audience} onChange={(v) => patch('audience', v)}
                        options={[{ label: `All paid (${paidCount})`, value: 'paid' }, { label: 'Selected orgs', value: 'selected' }]} />
                    </Col>
                  </Row>

                  {c.audience === 'selected' && (
                    <div style={{ marginTop: 16 }}>
                      <label className="wr-field-label">Selected organizations</label>
                      <Select mode="multiple" style={{ width: '100%' }} allowClear
                        placeholder="Pick organizations to include"
                        value={c.selectedOrgIds || []} onChange={(v) => patch('selectedOrgIds', v)}
                        options={orgs.map((o) => ({ label: `${o.name} · ${o.plan}`, value: o.id }))}
                        filterOption={(i, o) => (o?.label || '').toLowerCase().includes(i.toLowerCase())} />
                    </div>
                  )}

                  <Divider style={{ margin: '18px 0 14px' }} />

                  <div className="wr-guard" data-on={!!c.reviewGate}>
                    <div className="wr-switch-row">
                      <div>
                        <div className="wr-switch-label">
                          {c.reviewGate ? <ShieldCheck size={16} color={tone.green.fg} /> : <ShieldAlert size={16} color={tone.red.fg} />}
                          &nbsp;Review gate {c.reviewGate ? '(recommended)' : ''}
                        </div>
                        <div className="wr-switch-hint">
                          {c.reviewGate
                            ? 'Reports are generated as drafts. A human approves each one before it emails the client.'
                            : 'Reports send automatically. Findings can be wrong — leaving this off is not recommended.'}
                        </div>
                      </div>
                      <Switch checked={!!c.reviewGate} onChange={(v) => patch('reviewGate', v)} />
                    </div>
                    <div className="wr-switch-row" style={{ marginTop: 10 }}>
                      <div>
                        <div className="wr-switch-label"><AlertTriangle size={15} />&nbsp;Hold reports with a high red flag</div>
                        <div className="wr-switch-hint">Even with the gate off, any report carrying a high-severity finding waits for manual review.</div>
                      </div>
                      <Switch checked={!!c.autoSendBlockOnHighFlag} onChange={(v) => patch('autoSendBlockOnHighFlag', v)} />
                    </div>
                  </div>
                  {c.lastRunAt && <div className="wr-lastrun">Last run: {dayjs(c.lastRunAt).format('DD MMM YYYY, HH:mm')}</div>}
                </>
              )}
            </Card>

            {/* this week's run */}
            <Card className="wr-card" title={<Space><FileText size={17} /> <span>This week&rsquo;s run</span></Space>}
              extra={awaitingReview > 0 ? <Tag color="gold">{awaitingReview} awaiting review</Tag> : null}>
              <Table
                rowKey="_id" size="middle" columns={runColumns} dataSource={history.recent}
                loading={loading} pagination={{ pageSize: 8, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="No reports generated yet. Run a batch to create drafts." /> }}
              />
            </Card>

            {/* history */}
            <Card className="wr-card" title={<Space><Building2 size={17} /> <span>History by organization</span></Space>}
              extra={<span className="wr-s3">stored in S3 · <code>workforce-reports/&lt;orgId&gt;/</code></span>}>
              <Table
                rowKey="_id" size="middle" columns={historyColumns} dataSource={history.byOrg}
                loading={loading} pagination={{ pageSize: 8, hideOnSinglePage: true }}
                locale={{ emptyText: <Empty description="No reports sent yet." /> }}
              />
            </Card>

            <div className="wr-footnote">
              All access is read-only over your data; the module writes only its own logs and its own S3 folder.
              Personal content is never surfaced — reports show category and verdict only.
            </div>
          </div>

          {/* Approve & send — confirmation showing the exact recipient + mail draft before anything goes out */}
          <Modal
            open={sendModal.open}
            onCancel={closeSendConfirm}
            width={660}
            title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Send size={16} /> Approve &amp; send report</span>}
            footer={[
              <Button key="cancel" onClick={closeSendConfirm}>Cancel</Button>,
              <Button key="send" type="primary" danger icon={<Send size={14} />}
                loading={sendModal.sending}
                disabled={sendModal.loading || !(sendModal.draft && sendModal.draft.recipientResolved)}
                onClick={() => sendLog(sendModal.id)}>
                {sendModal.draft && sendModal.draft.recipientResolved
                  ? `Confirm & send to ${sendModal.draft.recipient.email}`
                  : 'Cannot send'}
              </Button>,
            ]}
          >
            {sendModal.loading || !sendModal.draft ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <div style={{ fontSize: 13 }}>
                {/* recipient */}
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.text2, marginBottom: 6 }}>This email will be sent to</div>
                {sendModal.draft.recipientResolved ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${tokens.border || '#e6e9ee'}`, borderRadius: 8, marginBottom: 16 }}>
                    <Mail size={18} style={{ color: '#0d6e6e', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{sendModal.draft.recipient.email}</div>
                      <div style={{ color: tokens.text2, fontSize: 12 }}>{sendModal.draft.recipient.name} · org admin of {sendModal.draft.organizationName}</div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', border: '1px solid #f0c2bd', background: '#fbeceb', color: '#a02d22', borderRadius: 8, marginBottom: 16 }}>
                    <AlertTriangle size={18} style={{ flexShrink: 0 }} />
                    <div>No org admin with a valid email was found for <b>{sendModal.draft.organizationName}</b>. Nothing can be sent until an org admin exists.</div>
                  </div>
                )}

                {/* subject */}
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.text2, marginBottom: 4 }}>Subject</div>
                <div style={{ fontWeight: 600, marginBottom: 14 }}>{sendModal.draft.subject}</div>

                {/* attachment */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  <Paperclip size={15} style={{ color: tokens.text2 }} />
                  <span style={{ fontWeight: 600 }}>{sendModal.draft.attachmentName}</span>
                  {sendModal.draft.attachmentAvailable ? (
                    <Tag color={sendModal.draft.attachmentType === 'pdf' ? 'green' : 'blue'}>
                      {sendModal.draft.attachmentType.toUpperCase()} · via {sendModal.draft.attachmentSource}
                    </Tag>
                  ) : (
                    <Tag color="orange">⚠ report not attached yet</Tag>
                  )}
                </div>

                {/* body preview */}
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.text2, marginBottom: 6 }}>Email preview</div>
                <iframe title="Email draft preview" srcDoc={sendModal.draft.bodyHtml}
                  style={{ width: '100%', height: 320, border: `1px solid ${tokens.border || '#e6e9ee'}`, borderRadius: 8, background: '#fff' }} />

                <div style={{ marginTop: 12, fontSize: 12, color: tokens.text2, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={13} /> This sends the report to a real customer contact and cannot be un-sent.
                </div>
              </div>
            )}
          </Modal>
        </div>
      </AdminThemeProvider>
    </>
  );
};

const PAGE_CSS = `
.wr-main{flex:1;overflow-y:auto;padding:26px 30px 60px;background:var(--admin-bg,transparent)}
.wr-header{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;margin-bottom:20px;flex-wrap:wrap}
.wr-title{font-size:24px;font-weight:700;letter-spacing:-.02em;margin:0}
.wr-sub{margin:4px 0 0;color:var(--admin-text-muted,#8a94a6);font-size:14px;max-width:70ch}
.wr-kpis{margin-bottom:20px}
.wr-kpi{border-radius:18px;padding:16px 18px;min-height:120px;box-sizing:border-box;transition:transform .18s ease,box-shadow .18s ease}
.wr-kpi:hover{transform:translateY(-2px)}
.wr-kpi-head{display:flex;align-items:center;gap:8px;margin-bottom:12px}
.wr-kpi-ico{width:28px;height:28px;border-radius:9px;display:inline-flex;align-items:center;justify-content:center;flex:0 0 28px}
.wr-kpi-label{font-size:12px;font-weight:600;letter-spacing:.01em}
.wr-kpi-value{font-size:28px;font-weight:800;letter-spacing:-.03em;line-height:1.05;font-variant-numeric:tabular-nums}
.wr-kpi-sub{font-size:11.5px;margin-top:7px;min-height:14px}
.wr-card{border-radius:16px;margin-bottom:18px}
.wr-switch-row{display:flex;align-items:center;justify-content:space-between;gap:20px}
.wr-switch-label{font-size:15px;font-weight:600;display:flex;align-items:center}
.wr-switch-hint{font-size:12.5px;color:var(--admin-text-muted,#8a94a6);margin-top:3px;max-width:64ch}
.wr-field-label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--admin-text-muted,#8a94a6);margin-bottom:7px}
.wr-field-hint{font-size:12px;color:var(--admin-text-muted,#8a94a6);margin-top:6px}
.wr-guard{border:1px solid var(--admin-border,#e6e9ee);border-radius:12px;padding:16px 18px}
.wr-guard[data-on="false"]{border-color:#e5514b55;background:rgba(229,81,75,.05)}
.wr-lastrun{margin-top:14px;font-size:12px;color:var(--admin-text-muted,#8a94a6)}
.wr-s3{font-size:12px;color:var(--admin-text-muted,#8a94a6)}
.wr-footnote{font-size:12px;color:var(--admin-text-muted,#8a94a6);line-height:1.6;margin-top:6px;max-width:80ch}
`;

export default WeeklyReports;
